<template>
  <v-card class="ma-0 pa-2 card mb-2" color="background" density="compact" variant="elevated" elevation="4">
    <v-card-item v-if="!expanded" class="schedule-summary" density="compact" @click="expanded = true">
      <template #prepend>
        <v-avatar size="40" rounded="lg" class="mr-3 schedule-summary-avatar">
          <v-icon icon="mdi-calendar-clock-outline" size="small"></v-icon>
        </v-avatar>
      </template>
      <v-card-title class="text-truncate">{{ localSchedule.label || 'New Factory Schedule' }}</v-card-title>
      <v-card-subtitle class="text-truncate">{{ scheduleSubtitle }}</v-card-subtitle>
      <template #append>
        <v-btn color="primary" density="compact" icon="mdi-chevron-down" size="small" variant="plain" @click.stop="expanded = true" />
      </template>
    </v-card-item>
    <v-card-text v-if="!expanded" class="pt-0 pb-2 schedule-summary-text">
      <div class="d-flex flex-wrap align-center">
        <v-chip class="mr-2 mb-2" density="compact" :color="localSchedule.status === 'enabled' ? 'success' : localSchedule.status === 'running' ? 'primary' : 'grey'" size="small">
          {{ localSchedule.status }}
        </v-chip>
        <v-chip v-if="displayTimestamp != null" class="mr-2 mb-2" density="compact" size="small" variant="tonal">
          {{ displayTimestampLabel }}
        </v-chip>
      </div>
    </v-card-text>
    <v-expand-transition>
      <div v-show="expanded" class="pa-1">
        <v-text-field v-model="localSchedule.label" label="Label" variant="outlined" density="compact" class="pt-2" />
        <v-select
          v-model="localSchedule.repeatInterval"
          :items="['once', 'daily', 'weekly', 'monthly']"
          label="Frequency"
          variant="outlined"
          density="compact"
          class="pt-2"
        />
        <v-row density="compact">
          <v-col cols="6" class="py-1" v-for="(time, index) in localTimesToRun" :key="index">
            <div class="d-flex ga-2 align-center">
              <v-number-input
                v-model="time.hour"
                label="Hour"
                :min="1"
                :max="12"
                control-variant="stacked"
                density="compact"
                hide-details
              />
              <v-number-input
                v-model="time.minute"
                label="Minute"
                :min="0"
                :max="59"
                control-variant="stacked"
                density="compact"
                hide-details
              />
              <v-select
                v-model="time.ampm"
                :items="['AM', 'PM']"
                :label="localTimeZoneAbbreviation"
                density="compact"
                hide-details
                variant="outlined"
              />
              <v-btn v-if="localTimesToRun.length > 1" density="compact" icon="mdi-minus-circle" variant="text" @click="removeTime(index)" />
            </div>
          </v-col>
        </v-row>
        <div class="text-caption text-medium-emphasis mb-2">
          Times are shown in your local timezone and stored in UTC automatically.
        </div>
        <v-btn size="small" density="compact" variant="outlined" prepend-icon="mdi-plus" class="mb-2" @click="addTime">Add Time</v-btn>
        <v-select
          v-if="localSchedule.repeatInterval === 'weekly'"
          v-model="weeklySelection"
          :items="weekdayItems"
          item-title="title"
          item-value="value"
          label="Days of Week"
          multiple
          chips
          variant="outlined"
          density="compact"
          class="pt-2"
        />
        <v-number-input
          v-if="localSchedule.repeatInterval === 'monthly'"
          v-model="localSchedule.dayOfMonth"
          label="Day of Month"
          :min="1"
          :max="31"
          control-variant="stacked"
          variant="outlined"
          density="compact"
          class="pt-2"
        />
        <v-textarea
          v-model="localSchedule.initialCarryPayload"
          label="Initial Carry Payload"
          auto-grow
          rows="2"
          variant="outlined"
          density="compact"
          class="pt-2"
        />
        <v-card-actions class="px-0 py-1">
          <v-btn color="primary" density="compact" variant="tonal" @click="save">Save</v-btn>
          <v-btn
            v-if="!isNew"
            color="primary"
            density="compact"
            variant="text"
            :disabled="localSchedule.status === 'running'"
            @click="emitToggle"
          >
            {{ localSchedule.status === 'enabled' ? 'Disable' : localSchedule.status === 'disabled' ? 'Enable' : 'Running' }}
          </v-btn>
          <v-btn v-if="!isNew" color="error" density="compact" variant="text" @click="emitDelete">Delete</v-btn>
          <v-btn v-if="isNew" density="compact" variant="text" @click="$emit('cancel-new')">Cancel</v-btn>
          <v-spacer />
          <v-btn density="compact" :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'" @click="expanded = !expanded" />
        </v-card-actions>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import type { FactoryScheduleDraft, FactoryScheduleRecord, NewFactorySchedulePayload } from '@/types/factories'
import {
  buildUtcDateOnReference,
  formatLocalDate as formatDateLocal,
  formatLocalTime as formatTimeLocal,
  getTimeZoneAbbreviation,
  type Local12HourTime,
  local12HourTimesToUtc,
  utcTimesToLocal12Hour
} from '@/utils/scheduleTime'

const props = withDefaults(defineProps<{
  schedule: FactoryScheduleRecord | FactoryScheduleDraft
  factoryId: string
  isNew?: boolean
}>(), {
  isNew: false
})

const emit = defineEmits<{
  (e: 'save', payload: FactoryScheduleRecord | NewFactorySchedulePayload): void
  (e: 'toggle', scheduleId: string): void
  (e: 'delete', scheduleId: string): void
  (e: 'cancel-new'): void
}>()

const localExpanded = ref(false)

const expanded = computed({
  get: () => props.isNew ? true : localExpanded.value,
  set: (value: boolean) => {
    localExpanded.value = value
  }
})
const localSchedule = reactive<FactoryScheduleRecord | FactoryScheduleDraft>(JSON.parse(JSON.stringify(props.schedule)))
const localTimesToRun = ref<Local12HourTime[]>([])

watch(
  () => props.schedule,
  (value) => {
    Object.assign(localSchedule, JSON.parse(JSON.stringify(value)))
    localTimesToRun.value = toLocalTimes(value)
  },
  { deep: true }
)

localTimesToRun.value = toLocalTimes(localSchedule)

const weekdayItems = [
  { title: 'Sun', value: 0 },
  { title: 'Mon', value: 1 },
  { title: 'Tue', value: 2 },
  { title: 'Wed', value: 3 },
  { title: 'Thu', value: 4 },
  { title: 'Fri', value: 5 },
  { title: 'Sat', value: 6 }
]

const weeklySelection = computed({
  get: () => localSchedule.daysOfWeek || [],
  set: (value: number[]) => {
    localSchedule.daysOfWeek = value
  }
})

function getReferenceDate(): Date {
  if (localSchedule.repeatInterval === 'once' && typeof localSchedule.startDate === 'number') {
    return new Date(localSchedule.startDate)
  }
  const timestamp =
    typeof localSchedule.nextRunAt === 'number'
      ? localSchedule.nextRunAt
      : typeof localSchedule.startDate === 'number'
        ? localSchedule.startDate
        : Date.now()
  return new Date(timestamp)
}

function toLocalTimes(schedule: FactoryScheduleRecord | FactoryScheduleDraft): Local12HourTime[] {
  const referenceDate = getReferenceDateFor(schedule)
  return utcTimesToLocal12Hour(schedule.timesToRun || [], referenceDate)
}

function getReferenceDateFor(schedule: FactoryScheduleRecord | FactoryScheduleDraft): Date {
  if (schedule.repeatInterval === 'once' && typeof schedule.startDate === 'number') {
    return new Date(schedule.startDate)
  }
  const timestamp =
    typeof schedule.nextRunAt === 'number'
      ? schedule.nextRunAt
      : typeof schedule.startDate === 'number'
        ? schedule.startDate
        : Date.now()
  return new Date(timestamp)
}

function getTimeDisplayDate(hour: number, minute: number): Date {
  const ref = getReferenceDate()
  return buildUtcDateOnReference(ref, { hour, minute })
}

function formatLocalEntryTime(time: Local12HourTime): string {
  const utcTime = local12HourTimesToUtc([time], getReferenceDate())[0]
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  }).format(getTimeDisplayDate(utcTime.hour, utcTime.minute))
}

const localTimeZoneAbbreviation = computed(() => getTimeZoneAbbreviation(getReferenceDate()))

const scheduleSubtitle = computed(() => {
  const times = localTimesToRun.value
    .map((time) => formatLocalEntryTime(time))
    .join(', ')
  return `${localSchedule.repeatInterval} · ${times}`
})

const displayTimestamp = computed<number | null>(() => {
  if (localSchedule.repeatInterval === 'once') {
    const firstTime = localSchedule.timesToRun[0]
    if (!firstTime) return typeof localSchedule.startDate === 'number' ? localSchedule.startDate : null
    return getTimeDisplayDate(firstTime.hour, firstTime.minute).getTime()
  }

  return typeof localSchedule.nextRunAt === 'number'
    ? localSchedule.nextRunAt
    : typeof localSchedule.startDate === 'number'
      ? localSchedule.startDate
      : null
})

const displayTimestampLabel = computed(() => {
  if (displayTimestamp.value == null) return ''
  const date = formatDateLocal(displayTimestamp.value)
  const time = formatTimeLocal(displayTimestamp.value)
  return localSchedule.repeatInterval === 'once'
    ? `Scheduled ${date} • ${time}`
    : `Next run ${date} • ${time}`
})

function addTime() {
  localTimesToRun.value.push({ hour: 12, minute: 0, ampm: 'PM' })
}

function removeTime(index: number) {
  localTimesToRun.value.splice(index, 1)
}

function save() {
  localSchedule.timesToRun = local12HourTimesToUtc(localTimesToRun.value, getReferenceDate())
  if (props.isNew) {
    const payload: NewFactorySchedulePayload = {
      factoryConfigId: props.factoryId,
      label: localSchedule.label,
      startDate: localSchedule.startDate,
      timesToRun: localSchedule.timesToRun,
      repeatInterval: localSchedule.repeatInterval,
      daysOfWeek: localSchedule.repeatInterval === 'weekly' ? localSchedule.daysOfWeek : undefined,
      dayOfMonth: localSchedule.repeatInterval === 'monthly' ? localSchedule.dayOfMonth : undefined,
      status: localSchedule.status,
      initialCarryPayload: localSchedule.initialCarryPayload
    }
    emit('save', payload)
    return
  }
  emit('save', { ...localSchedule })
}

function emitToggle() {
  if (typeof localSchedule.id === 'string' && localSchedule.id) {
    emit('toggle', localSchedule.id)
  }
}

function emitDelete() {
  if (typeof localSchedule.id === 'string' && localSchedule.id) {
    emit('delete', localSchedule.id)
  }
}
</script>
